import i18n, { InitOptions } from "i18next";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

import { setFrontendValidationLanguage } from "@dfp/isomorphic-zod";

import { DEFAULT_LANGUAGE, DEMO_MODE } from "../config.ts";

// Demo (Vercel): locale JSONs are not content-hashed but are served with a
// long immutable cache, so a returning visitor kept last release's strings.
// Version the URL with this bundle's hashed file name — it changes per build.
const DEMO_LOCALES_VERSION = DEMO_MODE
  ? (import.meta.url.split("/").pop() ?? "").replace(/\.js$/, "")
  : undefined;
import { languagesWithCImode } from "./languages.ts";

const KEYS_POST_PROCESSOR_NAME = "lokalise-in-context-keys";
let lokalisePostProcessorEnabled = false;

/**
 * Enables the Lokalise in-context keys post processor.
 * This transforms translations to show keys in the format {.namespace::key.}
 * which is recognized by the Lokalise in-context editor.
 */
export function enableLokalisePostProcessor() {
  lokalisePostProcessorEnabled = true;
}

const missingKeyHandler: InitOptions["missingKeyHandler"] = (
  lngs,
  ns,
  key,
  fallbackVal,
  updateMissing,
  opts,
) => {
  // oxlint-disable-next-line no-console
  console.warn(
    `i18n: Missing '${lngs.join(", ")}' translation for '${ns}:${key}'.`,
    opts,
  );
};

// for all options read: https://www.i18next.com/overview/configuration-options
const options: InitOptions = {
  defaultNS: undefined,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: [],
  supportedLngs: languagesWithCImode,
  // call missingKeyHandler on missing keys
  saveMissing: true,
  missingKeyHandler,
  // when translation is missing, show full key including the namespace
  appendNamespaceToMissingKey: true,
  // prefixes the namespace to the returned key when using lng: 'cimode'
  appendNamespaceToCIMode: true,
  interpolation: {
    // not needed for react as it escapes by default
    escapeValue: false,
  },
  backend: {
    loadPath: "/assets/locales/{{ns}}.{{lng}}.json",
    ...(DEMO_LOCALES_VERSION && {
      queryStringParams: { v: DEMO_LOCALES_VERSION },
    }),
  },
  load: "currentOnly",
  preload: [DEFAULT_LANGUAGE],

  ns: __I18N_TRANSITIVE_NAMESPACES__,
  react: {
    // Do NOT suspend the whole app while the ~47 namespace JSON files load.
    // With Suspense enabled, a single slow/failed locale fetch (a network
    // hiccup, a Vercel cold start, a dropped connection) leaves the top-level
    // splash spinning forever. With it off the app renders immediately and
    // translations pop in as they arrive — a brief untranslated flash at worst,
    // never an infinite "loading" screen.
    useSuspense: false,
  },
  postProcess: [KEYS_POST_PROCESSOR_NAME],
};

i18n
  .use({
    type: "postProcessor",
    name: KEYS_POST_PROCESSOR_NAME,
    process: (value: string, key: string, options: { ns: string }) => {
      if (lokalisePostProcessorEnabled) {
        const pair = `${options.ns}.${key}`;
        const transformed = pair.replaceAll(".", "::");
        return `{.${transformed}.}`;
      }

      return value;
    },
  })
  .use(initReactI18next)
  .use(HttpBackend)
  .init(options);

setFrontendValidationLanguage(() => i18n.language);

// With fallbackLng: [] and useSuspense: false, a namespace whose fetch fails
// (network blip, cold start) renders raw keys for the rest of the session —
// i18next never retries on its own. Retry failed namespace loads a few times
// with backoff so scattered untranslated keys heal without a manual reload.
const failedLoadRetries = new Map<string, number>();
const MAX_NS_RETRIES = 3;
i18n.on("failedLoading", (lng, ns) => {
  const retryKey = `${lng}:${ns}`;
  const attempt = failedLoadRetries.get(retryKey) ?? 0;
  if (attempt >= MAX_NS_RETRIES) return;
  failedLoadRetries.set(retryKey, attempt + 1);
  window.setTimeout(
    () => {
      i18n.reloadResources(lng, ns).then(() => {
        // Re-render translated components once the namespace finally arrives.
        i18n.emit("loaded", { [lng]: { [ns]: true } });
      });
    },
    2000 * 2 ** attempt,
  );
});
